import '../types';

import type { ColumnDef, ColumnSizingState } from '@tanstack/react-table';

import type {
	AddColumnTemplateDto,
	ViewColumnTemplateColumnDto,
	ViewColumnTemplateDto,
} from '@adflow/shared/util-column-templates';

export interface StoredColumnSettings extends ViewColumnTemplateColumnDto {
	id: string;
	/** Column['size'] */
	widthConfig?: number;
	/** Column['meta']['visible'] */
	visible?: boolean;
	/** Column['meta']['sticky'] */
	sticky?: boolean;
	/** Column['meta']['disableOrdering'] */
	disableOrdering?: boolean;
	/** Column['meta']['disableConfig'] */
	disableConfig?: boolean;
}

export function columnDefToStoredSettings<T>(
	column: ColumnDef<T>
): StoredColumnSettings {
	return {
		id: column.id!,
		widthConfig: column.size,
		visible: column.meta?.visible,
		sticky: column.meta?.sticky,
		disableOrdering: column.meta?.disableOrdering,
		disableConfig: column.meta?.disableConfig,
	};
}

type ColumnTemplateBase = Partial<
	Omit<ViewColumnTemplateDto, 'columns' | 'id'>
>;

/**
 * This is used to indicate we are using a local template and should be distinct
 * from `ViewColumnTemplateDto['id']`.
 */
export const localColumnTemplateId = 'local';

export interface LocalColumnTemplate extends ColumnTemplateBase {
	id: typeof localColumnTemplateId;
	columns: StoredColumnSettings[];

	savedAll?: false;
	savedDefault?: false;
}

export type ColumnTemplate = ViewColumnTemplateDto | LocalColumnTemplate;

export type ColumnTemplateId = ColumnTemplate['id'];

export function isRemoteColumnTemplateId(
	id: ColumnTemplateId | undefined
): id is ViewColumnTemplateDto['id'] {
	return typeof id === 'number';
}

export const DEFAULT_COLUMN_META = {
	visible: true,
	sticky: false,
};

interface ColumnTemplateChange extends AddColumnTemplateDto {
	id?: ColumnTemplateId;

	/**
	 * - If true, the template is saved to the database.
	 * - If false, the template is saved to only local storage.
	 */
	saved: boolean;
}

export type ChangeColumnTemplate = (dto: ColumnTemplateChange) => Promise<void>;

export type SelectColumnTemplate = (
	templateId: ColumnTemplateId | undefined
) => void;

export type ChangeColumnWidths = (widthMap: ColumnSizingState) => void;

export interface DataTableColumnProps<T> {
	columns: ColumnDef<T>[];
	columnTemplateMeta: {
		columnsBase: ColumnDef<T>[];
		columnWidths: ColumnSizingState;
		changeColumnTemplate: ChangeColumnTemplate;
		selectColumnTemplate: SelectColumnTemplate;
		changeColumnWidths: ChangeColumnWidths;
		resetColumnWidths: () => void;
		activeColumnTemplate: ColumnTemplate | undefined;
	};
}
